import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { SubmitButton } from "../../components/Input";
import { API_URL } from "../../config";
import StateManagedSelect from "react-select";
import { showToast } from "../../components/toast";
import LoadingSpinner from "../../components/LoadingSpinner";

const TemplateSmsForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [templateName, setTemplateName] = useState("");
  const [message, setMessage] = useState("");
  const [fieldlist, setFieldlist] = useState([]);
  const [loading, setLoading] = useState(false);
  const [senderList, setSenderList] = useState([]); // ✅ sender dropdown options
  const [selectedSender, setSelectedSender] = useState(null); // ✅ selected sender
  const token = localStorage.getItem("authToken");

  // ✅ Fetch SMS sender list
  const fetchSenderList = async () => {
    try {
      const response = await axios.get(`${API_URL}settings/emailconfig/dropdown`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const smsSenders =
        response.data?.data?.sms?.map((item) => ({
          value: Number(item.value), // ensure number for comparison
          label: `${item.label} (${item.sender})`,
          sender: item.sender,
        })) || [];

      setSenderList(smsSenders);

      // ✅ After sender list is loaded, fetch template (only if editing)
      if (id) {
        fetchTemplates(id, smsSenders);
      }
    } catch (error) {
      console.error("Error fetching SMS sender list:", error);
    }
  };

  // ✅ Fetch existing SMS template (for edit)
  const fetchTemplates = async (id, smsSenders = []) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}settings/template/sms/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const data = response.data.data;
      setTemplateName(data.templatename);
      setMessage(data.message);

      // ✅ Set selected sender based on sender_type
      if (data.sender_type && smsSenders.length > 0) {
        const foundSender = smsSenders.find(
          (opt) => opt.value === Number(data.sender_type)
        );
        setSelectedSender(foundSender || null);
      }
    } catch (error) {
      console.error("Error fetching template:", error);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Fetch SMS field list
  const fetchFieldList = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}settings/template/sms/dropdown`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      const formattedField = response.data.data.map((field) => ({
        value: field.field_name,
        label: field.field_description,
      }));
      setFieldlist(formattedField);
    } catch (error) {
      console.error("Error fetching field list:", error);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Load sender + field list first
  useEffect(() => {
    const loadData = async () => {
      await Promise.all([fetchSenderList(), fetchFieldList()]);
    };
    loadData();
  }, []);

  // ✅ Submit handler (create or update)
  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      templatename: templateName,
      message: message,
      sender_type: selectedSender ? selectedSender.value : "", // include sender
    };
    try {
      const url = id
        ? `${API_URL}settings/template/sms/update/${id}`
        : `${API_URL}settings/template/sms/save`;

      const response = await axios({
        method: id ? "put" : "post",
        url,
        data: payload,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.data.success === true) {
        showToast.success(response.data.message);
        navigate("/Settings/TemplateSms");
      }
    } catch (error) {
      console.error("Error saving template SMS:", error);
      showToast.error("There was an error saving the template");
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div id="page-container" className="page-header-fixed page-sidebar-fixed fade in">
      <div id="content" className="">
        <div className="panel panel-default with-rounded-corner with-shadow">
          <form onSubmit={handleSubmit} encType="multipart/form-data">
            <input type="hidden" name="id" value={id} />
            <div className="panel-heading border-0">
              <span className="panel-title">
                {id ? "Edit " : "Add "} Template SMS
              </span>
            </div>
            <div className="panel-body">
              <div className="row">
                {/* Template Name */}
                <div className="col-md-4">
                  <div className="form-group">
                    <label>Template Name</label>
                    <input
                      type="text"
                      name="templatename"
                      className="form-control"
                      placeholder="Enter Template Name"
                      value={templateName}
                      onChange={(e) => setTemplateName(e.target.value)}
                    />
                  </div>
                </div>

                {/* ✅ Sender Dropdown */}
                <div className="col-md-4">
                  <div className="form-group">
                    <label>Sender Type</label>
                    <StateManagedSelect
                      options={senderList}
                      value={selectedSender}
                      onChange={setSelectedSender}
                      placeholder="Select SMS Sender"
                      isClearable
                    />
                  </div>
                </div>

                {/* Select Data */}
                <div className="col-md-4">
                  <div className="form-group">
                    <label>Select Data</label>
                    <StateManagedSelect
                      options={fieldlist}
                      onChange={(selectedOption) => {
                        if (selectedOption) {
                          setMessage((prev) => `${prev}${selectedOption.value}`);
                        }
                      }}
                    />
                  </div>
                </div>

                {/* Message Textarea */}
                <div className="col-md-12">
                  <div className="form-group">
                    <label>Message</label>
                    <textarea
                      name="message"
                      className="form-control"
                      placeholder="Enter Message"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      rows="5"
                    />
                  </div>
                </div>

                {/* Submit Button */}
                <div className="col-md-12">
                  <SubmitButton
                    pageBack={"/Settings/TemplateSms"}
                    handleSubmit={handleSubmit}
                  />
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TemplateSmsForm;
